import moment from 'moment';

import { SEARCHING_COUNTRY_SELECTION } from '../constants';

export const makeRules = ({
  isRequiredProperty,
  ruleType = ['default', 'formList', 'list'][0],
  minItems,
}) => {
  let rules = [];

  if (isRequiredProperty) {
    rules = [
      ...rules,
      {
        required: true,
        message: 'Required Field',
      },
    ];
  }

  if (ruleType === 'list') {
    rules = [
      ...rules,
      {
        validator: async (_, value) => {
          if (value?.length < minItems) {
            return Promise.reject(
              new Error(`Require at least [${minItems}] item(s)`)
            );
          }
        },
      },
    ];
  }

  if (ruleType === 'formList') {
    rules = [
      ...rules,
      {
        validator: async (_, formList) => {
          if (formList?.length < Number(minItems)) {
            return Promise.reject(
              new Error(`Require at least [${minItems}] item(s)`)
            );
          }
        },
      },
    ];
  }

  return rules;
};

export const filterOptionHandler = (enums, fullKey) => (input, option) => {
  const label = option.children?.trim()?.toLowerCase();
  const formattedInput = input.trim().toLowerCase();

  if (SEARCHING_COUNTRY_SELECTION.includes(fullKey)) {
    return filterCountryOption(enums, label, formattedInput);
  }
  return label.indexOf(formattedInput) >= 0;
};

const filterCountryOption = (enums, label, formattedInput) => {
  if (!enums) return;
  const [code, countryFullName] = label.split(' - ');

  if (formattedInput.includes('-')) {
    return label?.includes(formattedInput);
  }

  const codeMatches = code?.startsWith(formattedInput);
  const nameMatches = countryFullName?.includes(formattedInput);

  return (
    codeMatches ||
    (!enums.some((opt) =>
      opt.trim()?.toLowerCase()?.startsWith(formattedInput)
    ) &&
      nameMatches)
  );
};

export const flattenWalmartSchema = (list) => {
  let arr = [];

  for (let i = 0; i < list?.length; i++) {
    arr.push(list?.[i]);
    if (list?.[i]?.childProperties?.length) {
      arr = arr.concat(flattenWalmartSchema(list?.[i]?.childProperties));
    } else {
      if (list?.[i]?.items?.childProperties?.length) {
        arr = arr.concat(
          flattenWalmartSchema(list?.[i]?.items?.childProperties)
        );
      }
    }
  }

  return arr;
};

export const flattenAndConvert = (arr, result = {}) => {
  function traverse(node) {
    if (!node) return;

    const { fullKey, items, childProperties } = node;
    result[fullKey] = node;

    if (childProperties) {
      childProperties?.forEach(traverse);
    }

    if (items?.childProperties) {
      items.childProperties?.forEach(traverse);
    }
  }

  arr?.forEach(traverse);
  return result;
};

const processObject = (obj, schema, parentKey) => {
  let formattedObj = {};
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const fullKey = parentKey ? `${parentKey}.${key}` : key;
      const value = obj[key];

      if (schema[fullKey] && schema[fullKey].format === 'date-time') {
        formattedObj[key] = moment(value);
      } else {
        formattedObj[key] = formatDatesRecursive(value, schema, fullKey);
      }
    }
  }
  return formattedObj;
};

export const formatDatesRecursive = (obj, schema, parentKey = '') => {
  if (Array.isArray(obj)) {
    return obj.map((item) => formatDatesRecursive(item, schema, parentKey));
  } else if (typeof obj === 'object' && obj !== null) {
    return processObject(obj, schema, parentKey);
  }

  return obj;
};

export const cleanInvalidData = (arr) => {
  return arr.map((item) => {
    if (!item || typeof item !== 'object') return item;

    let { productId, data } = item;
    if (!data) return item;

    let cleanedOrderable = removeInvalidValueKey(data.orderable);
    let cleanedVisible = removeInvalidValueKey(data.visible);
    let cleanedTradeItem = removeInvalidValueKey(data.tradeItem);

    let cleanedData = {
      orderable: cleanedOrderable ?? {},
      visible: cleanedVisible ?? {},
      tradeItem: cleanedTradeItem ?? {},
    };

    return { productId, data: cleanedData };
  });
};

export const removeInvalidValueKey = (obj) => {
  if (moment.isMoment(obj)) {
    return obj.isValid() ? obj : undefined;
  }

  if (Array.isArray(obj)) {
    let cleanedArray = obj
      .map(removeInvalidValueKey)
      .filter((item) => !isEmptyValue(item));

    return cleanedArray.length > 0 ? cleanedArray : [];
  } else if (typeof obj === 'object' && obj !== null) {
    let cleanedObj = Object.fromEntries(
      Object.entries(obj)
        .map(([key, value]) => [
          key,
          moment.isMoment(value) && value.isValid()
            ? value
            : removeInvalidValueKey(value),
        ])
        .filter(([_, value]) => !isEmptyValue(value))
    );

    return cleanedObj;
  }

  return obj;
};

const isEmptyValue = (value) => {
  return (
    value === null ||
    value === undefined ||
    value === '' ||
    (Array.isArray(value) && value.length === 0) ||
    (typeof value === 'object' &&
      !(moment.isMoment(value) && value.isValid()) &&
      Object.keys(value).length === 0)
  );
};
